import Service from '@ember/service';
import { service } from '@ember/service';
import { waitForPromise } from '@ember/test-waiters';

import type LoggingService from './logger';
import type AnalyticsService from '@ebiz/ember-aditools-analytics/services/aditools-analytics';

export interface ToolsAPIResponse<T> {
  status: number;
  data?: T;
}
export default class ToolsAPIService extends Service {
  @service('aditools-analytics') declare analytics: AnalyticsService;
  @service('aditools-site') declare siteconfig: { [key: string]: string };
  @service declare logger: LoggingService;
  abortController: AbortController = new AbortController();

  willDestroy(): void {
    this.abortController.abort();
  }

  getResponse(process: string, data: string): Promise<Response> {
    return waitForPromise(
      fetch(`${this.siteconfig['ApiUrl']}/${process}/${this.siteconfig['AdiLocale']}`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-store',
        },
        body: data,
        method: 'POST',
        signal: this.abortController.signal,
      }),
    );
  }

  //download
  async downloadDesign(process: string, data: string): Promise<[string, string, Blob | null]> {
    const processWord = process || 'download';
    try {
      const response = await this.getResponse(processWord, '"' + encodeURIComponent(data) + '"');
      if (response.ok) {
        const contentType = response.headers.get('Content-Type');
        const contentDisposition = response.headers.get('Content-Disposition');
        let fileName = '';
        if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches !== null && matches[1]) {
            fileName = matches[1].replace(/['"]/g, '');
          } else {
            fileName = 'DesignFiles.zip';
          }
        } else {
          fileName = 'DesignFiles.zip';
        }
        const responseBlob = await response.blob();
        return Promise.resolve([contentType ? contentType : '', fileName, responseBlob]);
      } else {
        return Promise.resolve(['', '', null]);
      }
    } catch (error: unknown) {
      const namedError = error as { message: string };
      this.logger.error(`Download failed: ${namedError.message}`);
      return Promise.resolve(['', '', null]);
    }
  }

  // feedback
  deliverFeedback(process: string, name: string, email: string, feedback: string): Promise<ToolsAPIResponse<unknown>> {
    const processWord = process || 'feedback';
    const feedbackData = {
      ToolId: 2,
      Name: encodeURIComponent(name),
      Email: encodeURIComponent(email),
      Feedback: encodeURIComponent(feedback),
    };
    return this.getResponse(processWord, JSON.stringify(feedbackData))
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        this.logger.error(`Give feedback failed: ${error}`);
        return { status: -1 };
      });
  }

  // share
  shareDesign(
    process: string,
    name: string,
    sender: string,
    recipients: string,
    design: Record<string, unknown>,
  ): Promise<ToolsAPIResponse<unknown>> {
    const processWord = process || 'share';
    const now = new Date();
    const ticks = now.getTime();
    const fileName = name.length > 0 ? name.replace(/[/\\?*:|"<>]/g, '') : 'SharedDesign_PD' + ticks.toString();
    const saveDateStamp =
      now.getFullYear() +
      '-' +
      ('0' + (now.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + now.getDate()).slice(-2) +
      ' ' +
      ('0' + (((now.getHours() + 11) % 12) + 1)).slice(-2) +
      ':' +
      ('0' + now.getMinutes()).slice(-2) +
      ':' +
      ('0' + now.getSeconds()).slice(-2) +
      ' ' +
      (now.getHours() >= 12 ? 'PM' : 'AM');
    const toolName = 'PD';
    const toolVersion = '1.1';
    const jsonVersion = '1.0.0';
    const shareData = {
      ToolId: 2,
      Name: name,
      Description: name,
      FileName: fileName,
      DesignData: {
        Name: name,
        Description: name,
        Tool: toolName,
        Version: toolVersion,
        Design: design,
        CreatedDate: saveDateStamp,
        UpdatedDate: saveDateStamp,
        JsonVersion: jsonVersion,
      },
      EmailRecipients: recipients,
      SenderName: sender,
    };
    return this.getResponse(processWord, '"' + encodeURIComponent(JSON.stringify(shareData)) + '"')
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        this.logger.error(`Share design failed: ${error}`);
        return { status: -1 };
      });
  }
}

declare module '@ember/service' {
  interface Registry {
    toolsapi: ToolsAPIService;
  }
}
